import axios from 'axios';

// REACT_APP_API_URL is baked in at build time by CRA. In a prod build we
// require it — falling back to localhost in prod would route real users'
// traffic at their own machine. Dev builds still accept the default.
const IS_PROD_BUILD = process.env.NODE_ENV === 'production';
if (IS_PROD_BUILD && !process.env.REACT_APP_API_URL) {
  throw new Error('FATAL: REACT_APP_API_URL must be set at build time for production');
}
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Include cookies for refresh tokens
  headers: {
    'Content-Type': 'application/json',
  },
});

function getAuthenticatedConfig(config = {}) {
  const token = localStorage.getItem('accessToken');
  return {
    withCredentials: true,
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
      originalRequest._retry = true;

      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        localStorage.setItem('accessToken', data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API Client
const apiClient = {
  // Auth endpoints
  async googleAuth(token) {
    const { data } = await api.post('/auth/google', { token });
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  async devLogin() {
    const { data } = await api.post('/auth/dev-login');
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      this.clearTokens();
    }
  },

  async refreshSession() {
    const { data } = await api.post('/auth/refresh', {});
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
    }
    return data;
  },

  async getCurrentUser() {
    const { data } = await api.get('/auth/me');
    return data;
  },

  // User endpoints
  async getUsers() {
    const { data } = await api.get('/users');
    return data;
  },

  async getActiveUsers() {
    const { data } = await api.get('/users/active');
    return data;
  },
  async getTeams() {
    const { data } = await api.get('/teams');
    return data;
  },

  async createTeam(teamData) {
    const { data } = await api.post('/teams', teamData);
    return data;
  },

  async updateTeam(teamId, teamData) {
    const { data } = await api.patch(`/teams/${teamId}`, teamData);
    return data;
  },

  async addTeamMember(teamId, memberData) {
    const { data } = await api.post(`/teams/${teamId}/members`, memberData);
    return data;
  },

  async removeTeamMember(teamId, userId) {
    const { data } = await api.delete(`/teams/${teamId}/members/${userId}`);
    return data;
  },

  async inviteUser(email, role) {
    const { data } = await api.post('/users/invite', { email, role });
    return data;
  },

  async updateUserRole(userId, role) {
    const { data } = await api.patch(`/users/${userId}/role`, { role });
    return data;
  },

  async updateUserStatus(userId, status) {
    const { data } = await api.patch(`/users/${userId}/status`, { status });
    return data;
  },

  async deleteUser(userId) {
    const { data } = await api.delete(`/users/${userId}`);
    return data;
  },

  // Client endpoints
  async getClients() {
    const { data } = await api.get('/clients');
    return data;
  },

  async getClient(clientId) {
    const { data } = await api.get(`/clients/${clientId}`);
    return data;
  },

  async createClient(clientData) {
    const { data } = await api.post('/clients', clientData);
    return data;
  },

  async updateClient(clientId, clientData) {
    const { data } = await api.patch(`/clients/${clientId}`, clientData);
    return data;
  },

  async deleteClient(clientId) {
    const { data } = await api.delete(`/clients/${clientId}`);
    return data;
  },

  // Project endpoints
  async getProjects() {
    const { data } = await api.get('/projects');
    return data;
  },

  async getProject(projectId) {
    const { data } = await api.get(`/projects/${projectId}`);
    return data;
  },

  async createProject(projectData) {
    const { data } = await api.post('/projects', projectData);
    return data;
  },

  async pushProjectToTracker(projectId) {
    const { data } = await api.post(`/projects/${projectId}/push-to-tracker`);
    return data;
  },

  async updateProject(projectId, projectData) {
    const { data } = await api.patch(`/projects/${projectId}`, projectData);
    return data;
  },

  async deleteProject(projectId) {
    const { data } = await api.delete(`/projects/${projectId}`);
    return data;
  },

  // Project assignments (consultants ↔ projects)
  async listProjectAssignments(params = {}) {
    const { data } = await api.get('/project-assignments', { params });
    return data;
  },
  async createProjectAssignment(payload) {
    const { data } = await api.post('/project-assignments', payload);
    return data;
  },
  async updateProjectAssignment(id, payload) {
    const { data } = await api.patch(`/project-assignments/${id}`, payload);
    return data;
  },
  async deleteProjectAssignment(id) {
    const { data } = await api.delete(`/project-assignments/${id}`);
    return data;
  },

  // System settings (assignmentSource flag, etc.)
  async getSystemSettings() {
    const { data } = await api.get('/system-settings');
    return data;
  },
  async updateAssignmentSource(value) {
    const { data } = await api.put('/system-settings/assignment-source', { value });
    return data;
  },

  // Audit log
  async getAuditLog(params = {}) {
    const { data } = await api.get('/audit-log', { params });
    return data;
  },

  // Project comments
  async listProjectComments(projectId) {
    const { data } = await api.get(`/projects/${projectId}/comments`);
    return data;
  },
  async addProjectComment(projectId, payload) {
    // Accepts string (legacy) or { content, mentions } (rich).
    const body = typeof payload === 'string' ? { text: payload } : payload;
    const { data } = await api.post(`/projects/${projectId}/comments`, body);
    return data;
  },
  async deleteProjectComment(commentId) {
    const { data } = await api.delete(`/projects/comments/${commentId}`);
    return data;
  },

  // Task endpoints
  async getTasks(filters = {}) {
    const { data } = await api.get('/tasks', { params: filters });
    return data;
  },

  async getTask(taskId) {
    const { data } = await api.get(`/tasks/${taskId}`);
    return data;
  },

  async createTask(taskData) {
    const { data } = await api.post('/tasks', taskData);
    return data;
  },

  async updateTask(taskId, taskData) {
    const { data } = await api.patch(`/tasks/${taskId}`, taskData);
    return data;
  },

  async deleteTask(taskId) {
    const { data } = await api.delete(`/tasks/${taskId}`);
    return data;
  },

  // Deal endpoints
  async getDeals() {
    const { data } = await api.get('/deals');
    return data;
  },

  async getDeal(dealId) {
    const { data } = await api.get(`/deals/${dealId}`);
    return data;
  },

  async createDeal(dealData) {
    const { data } = await api.post('/deals', dealData);
    return data;
  },

  async updateDeal(dealId, dealData) {
    const { data } = await api.patch(`/deals/${dealId}`, dealData);
    return data;
  },

  async deleteDeal(dealId) {
    const { data } = await api.delete(`/deals/${dealId}`);
    return data;
  },

  // Scoping endpoints (board-like functionality)
  async getScoping() {
    const { data } = await api.get('/scoping');
    return data;
  },

  async getScopingItem(scopingId) {
    const { data } = await api.get(`/scoping/${scopingId}`);
    return data;
  },

  async createScoping(scopingData) {
    const { data } = await api.post('/scoping', scopingData);
    return data;
  },

  async updateScoping(scopingId, scopingData) {
    const { data } = await api.patch(`/scoping/${scopingId}`, scopingData);
    return data;
  },

  async deleteScoping(scopingId) {
    const { data } = await api.delete(`/scoping/${scopingId}`);
    return data;
  },

  // Convert a DONE scoping into Project + Client (+ assignments). If
  // SITUATION mode is active, the backend also enqueues outbox entries
  // so the project/members push to tracker on the next worker tick.
  async convertScopingToProject(scopingId, { clientName, projectName } = {}) {
    const { data } = await api.post(`/scoping/${scopingId}/convert`, { clientName, projectName });
    return data;
  },

  // Scoping tasks (atomic operations)
  async createScopingTask(scopingId, taskData) {
    const { data } = await api.post(`/scoping/${scopingId}/tasks`, taskData);
    return data;
  },

  async batchCreateScopingTasks(scopingId, tasks) {
    const { data } = await api.post(`/scoping/${scopingId}/tasks/batch`, { tasks });
    return data;
  },

  async updateScopingTask(taskId, taskData) {
    const { data } = await api.patch(`/scoping/tasks/${taskId}`, taskData);
    return data;
  },

  async deleteScopingTask(taskId) {
    const { data } = await api.delete(`/scoping/tasks/${taskId}`);
    return data;
  },

  async toggleScopingTaskDone(taskId, done) {
    const { data } = await api.patch(`/scoping/tasks/${taskId}/done`, { done });
    return data;
  },

  async assignScopingTask(taskId, assigneeId) {
    const { data } = await api.patch(`/scoping/tasks/${taskId}/assign`, { assigneeId });
    return data;
  },

  async addScopingTaskComment(taskId, payload) {
    const body = typeof payload === 'string' ? { content: payload } : payload;
    const { data } = await api.post(`/scoping/tasks/${taskId}/comments`, body);
    return data;
  },

  async deleteScopingTaskComment(commentId) {
    const { data } = await api.delete(`/scoping/comments/${commentId}`);
    return data;
  },

  // Scoping subtasks
  async createScopingSubtask(taskId, subtaskData) {
    const { data } = await api.post(`/scoping/tasks/${taskId}/subtasks`, subtaskData);
    return data;
  },

  async updateScopingSubtask(subtaskId, subtaskData) {
    const { data } = await api.patch(`/scoping/subtasks/${subtaskId}`, subtaskData);
    return data;
  },

  async deleteScopingSubtask(subtaskId) {
    const { data } = await api.delete(`/scoping/subtasks/${subtaskId}`);
    return data;
  },

  async toggleScopingSubtaskDone(subtaskId, done) {
    const { data } = await api.patch(`/scoping/subtasks/${subtaskId}/done`, { done });
    return data;
  },

  async addScopingSubtaskComment(subtaskId, payload) {
    const body = typeof payload === 'string' ? { content: payload } : payload;
    const { data } = await api.post(`/scoping/subtasks/${subtaskId}/comments`, body);
    return data;
  },

  async deleteScopingSubtaskComment(commentId) {
    const { data } = await api.delete(`/scoping/subtask-comments/${commentId}`);
    return data;
  },

  // Scoping attachments
  async addScopingTaskAttachment(taskId, attachmentData) {
    const { data } = await api.post(`/scoping/tasks/${taskId}/attachments`, attachmentData);
    return data;
  },

  async deleteScopingTaskAttachment(attachmentId) {
    const { data } = await api.delete(`/scoping/task-attachments/${attachmentId}`);
    return data;
  },

  async addScopingSubtaskAttachment(subtaskId, attachmentData) {
    const { data } = await api.post(`/scoping/subtasks/${subtaskId}/attachments`, attachmentData);
    return data;
  },

  async deleteScopingSubtaskAttachment(attachmentId) {
    const { data } = await api.delete(`/scoping/subtask-attachments/${attachmentId}`);
    return data;
  },

  // Lead endpoints
  async getLeads() {
    const { data } = await api.get('/leads');
    return data;
  },

  async getLead(leadId) {
    const { data } = await api.get(`/leads/${leadId}`);
    return data;
  },

  async createLead(leadData) {
    const { data } = await api.post('/leads', leadData);
    return data;
  },

  async updateLead(leadId, leadData) {
    const { data } = await api.patch(`/leads/${leadId}`, leadData);
    return data;
  },

  async createLeadTask(leadId, taskData) {
    const { data } = await api.post(`/leads/${leadId}/tasks`, taskData);
    return data;
  },

  async updateLeadTask(leadId, taskId, taskData) {
    const { data } = await api.patch(`/leads/${leadId}/tasks/${taskId}`, taskData);
    return data;
  },

  async deleteLeadTask(leadId, taskId) {
    const { data } = await api.delete(`/leads/${leadId}/tasks/${taskId}`);
    return data;
  },

  async createLeadSubtask(leadId, taskId, subtaskData) {
    const { data } = await api.post(`/leads/${leadId}/tasks/${taskId}/subtasks`, subtaskData);
    return data;
  },

  async updateLeadSubtask(leadId, taskId, subtaskId, subtaskData) {
    const { data } = await api.patch(`/leads/${leadId}/tasks/${taskId}/subtasks/${subtaskId}`, subtaskData);
    return data;
  },

  async deleteLeadSubtask(leadId, taskId, subtaskId) {
    const { data } = await api.delete(`/leads/${leadId}/tasks/${taskId}/subtasks/${subtaskId}`);
    return data;
  },

  async createLeadNote(leadId, noteData) {
    const { data } = await api.post(`/leads/${leadId}/notes`, noteData);
    return data;
  },

  async deleteLead(leadId) {
    const { data } = await api.delete(`/leads/${leadId}`);
    return data;
  },

  // Audit endpoints
  async getAudits() {
    const { data } = await api.get('/audits');
    return data;
  },

  async getAudit(auditId) {
    const { data } = await api.get(`/audits/${auditId}`);
    return data;
  },

  async createAudit(auditData) {
    const { data } = await api.post('/audits', auditData);
    return data;
  },

  async updateAudit(auditId, auditData) {
    const { data } = await api.patch(`/audits/${auditId}`, auditData);
    return data;
  },

  async deleteAudit(auditId) {
    const { data } = await api.delete(`/audits/${auditId}`);
    return data;
  },

  // Audit Subitem endpoints
  async createAuditSubitem(auditId, subitemData) {
    const { data } = await api.post(`/audits/${auditId}/subitems`, subitemData);
    return data;
  },
  async updateAuditSubitem(auditId, subitemId, subitemData) {
    const { data } = await api.patch(`/audits/${auditId}/subitems/${subitemId}`, subitemData);
    return data;
  },
  async deleteAuditSubitem(auditId, subitemId) {
    const { data } = await api.delete(`/audits/${auditId}/subitems/${subitemId}`);
    return data;
  },

  // Audit Comment endpoints
  async createAuditComment(auditId, commentData) {
    const { data } = await api.post(`/audits/${auditId}/comments`, commentData, getAuthenticatedConfig());
    return data;
  },
  async updateAuditComment(auditId, commentId, content) {
    const { data } = await api.patch(`/audits/${auditId}/comments/${commentId}`, { content }, getAuthenticatedConfig());
    return data;
  },
  async deleteAuditComment(auditId, commentId) {
    const { data } = await api.delete(`/audits/${auditId}/comments/${commentId}`, getAuthenticatedConfig());
    return data;
  },

  // Audit task template categories
  async getTaskTemplateCategories() {
    const { data } = await api.get('/audits/meta/task-template-categories');
    return data;
  },
  async createTaskTemplateCategory(categoryData) {
    const { data } = await api.post('/audits/meta/task-template-categories', categoryData);
    return data;
  },
  async updateTaskTemplateCategory(categoryId, categoryData) {
    const { data } = await api.patch(`/audits/meta/task-template-categories/${categoryId}`, categoryData);
    return data;
  },
  async deleteTaskTemplateCategory(categoryId) {
    const { data } = await api.delete(`/audits/meta/task-template-categories/${categoryId}`);
    return data;
  },

  // Task templates
  async createTaskTemplate(templateData) {
    const { data } = await api.post('/audits/meta/task-templates', templateData);
    return data;
  },
  async updateTaskTemplate(templateId, templateData) {
    const { data } = await api.patch(`/audits/meta/task-templates/${templateId}`, templateData);
    return data;
  },
  async deleteTaskTemplate(templateId) {
    const { data } = await api.delete(`/audits/meta/task-templates/${templateId}`);
    return data;
  },

  // Audit tasks
  async generateAuditTasks(auditId, payload) {
    const { data } = await api.post(`/audits/${auditId}/tasks/generate`, payload);
    return data;
  },
  async createAuditTask(auditId, taskData) {
    const { data } = await api.post(`/audits/${auditId}/tasks`, taskData);
    return data;
  },
  async updateAuditTask(auditId, taskId, taskData) {
    const { data } = await api.patch(`/audits/${auditId}/tasks/${taskId}`, taskData);
    return data;
  },
  async deleteAuditTask(auditId, taskId) {
    const { data } = await api.delete(`/audits/${auditId}/tasks/${taskId}`);
    return data;
  },
  async createAuditTaskComment(auditId, taskId, commentData) {
    const { data } = await api.post(`/audits/${auditId}/tasks/${taskId}/comments`, commentData, getAuthenticatedConfig());
    return data;
  },
  async updateAuditTaskComment(auditId, taskId, commentId, commentData) {
    const { data } = await api.patch(`/audits/${auditId}/tasks/${taskId}/comments/${commentId}`, commentData, getAuthenticatedConfig());
    return data;
  },
  async deleteAuditTaskComment(auditId, taskId, commentId) {
    const { data } = await api.delete(`/audits/${auditId}/tasks/${taskId}/comments/${commentId}`, getAuthenticatedConfig());
    return data;
  },

  // Audit Type endpoints
  async getAuditTypes() {
    const { data } = await api.get('/audits/meta/types');
    return data;
  },
  async createAuditType(name) {
    const { data } = await api.post('/audits/meta/types', { name });
    return data;
  },
  async deleteAuditType(id) {
    const { data } = await api.delete(`/audits/meta/types/${id}`);
    return data;
  },

  // Language endpoints
  async getLanguages() {
    const { data } = await api.get('/audits/meta/languages');
    return data;
  },
  async createLanguage(name) {
    const { data } = await api.post('/audits/meta/languages', { name });
    return data;
  },

  // StatusValue endpoints
  async getStatusValues(module) {
    const { data } = await api.get('/audits/meta/status-values', { params: module ? { module } : undefined });
    return data;
  },
  async createStatusValue(statusData) {
    const { data } = await api.post('/audits/meta/status-values', statusData);
    return data;
  },
  async updateStatusValue(id, statusData) {
    const { data } = await api.patch(`/audits/meta/status-values/${id}`, statusData);
    return data;
  },
  async deleteStatusValue(id) {
    const { data } = await api.delete(`/audits/meta/status-values/${id}`);
    return data;
  },
  async reorderStatusValues(module, ids) {
    const { data } = await api.post(`/audits/meta/status-values/reorder/${module}`, { ids });
    return data;
  },

  // Consultant endpoints
  async getConsultants() {
    const { data } = await api.get('/consultants');
    return data;
  },

  async getConsultant(consultantId) {
    const { data } = await api.get(`/consultants/${consultantId}`);
    return data;
  },

  async createConsultant(consultantData) {
    const { data } = await api.post('/consultants', consultantData);
    return data;
  },

  async updateConsultant(consultantId, consultantData) {
    const { data } = await api.patch(`/consultants/${consultantId}`, consultantData);
    return data;
  },

  async deleteConsultant(consultantId) {
    const { data } = await api.delete(`/consultants/${consultantId}`);
    return data;
  },

  // Weekly Report endpoints
  async getWeeklyReports() {
    const { data } = await api.get('/weekly-reports');
    return data;
  },

  async getWeeklyReport(reportId) {
    const { data } = await api.get(`/weekly-reports/${reportId}`);
    return data;
  },

  async createWeeklyReport(reportData) {
    const { data } = await api.post('/weekly-reports', reportData);
    return data;
  },

  async updateWeeklyReport(reportId, reportData) {
    const { data } = await api.patch(`/weekly-reports/${reportId}`, reportData);
    return data;
  },

  async deleteWeeklyReport(reportId) {
    const { data } = await api.delete(`/weekly-reports/${reportId}`);
    return data;
  },

  // Financial Status endpoints
  async getFinancialStatus(year) {
    const { data } = await api.get('/financial-status', { params: { year } });
    return data;
  },

  async upsertFinancialStatusEntry(entryData) {
    const { data } = await api.post('/financial-status', entryData);
    return data;
  },

  // codex.md Financial Board — actual/expected/forecast series per month,
  // plus derived profit. revenueActual is read-only (tracker-owned).
  async getMonthlyFinancials(year) {
    const { data } = await api.get('/financial-status/monthly', { params: { year } });
    return data;
  },

  async updateMonthlyFinancials(payload) {
    const { data } = await api.patch('/financial-status/monthly', payload);
    return data;
  },

  async syncInvoicesNow() {
    // Manual tracker invoice pull → revenueActual. Returns {months,created,updated,failed}.
    const { data } = await api.post('/financial-status/sync-invoices');
    return data;
  },

  async getMonthlyBillingMonday(year) {
    // Per-month aggregated Monday billing snapshot — { actual, expected, forecast, hasData }.
    const { data } = await api.get('/financial-status/monthly-billing', { params: { year } });
    return data;
  },

  async getTopClients(year, limit = 5) {
    // codex.md — top N clients by YTD invoiced SEK, from MondayBillingSnapshot.
    const { data } = await api.get('/financial-status/top-clients', { params: { year, limit } });
    return data;
  },

  async syncMondayBillingNow() {
    // Manual Monday board pull → MondayBillingSnapshot rows. Same
    // function the daily cron invokes. Gated to SUPER_ADMIN/CEO/CFO.
    const { data } = await api.post('/monday/billing-sync');
    return data;
  },

  // codex.md RBAC. /me returns the caller's merged {pages, graphs}. The grid
  // endpoints are SUPER_ADMIN-only (backend enforces; UI mirrors).
  async getMyPermissions() {
    const { data } = await api.get('/permissions/me');
    return data;
  },

  async getPermissionGrid() {
    const { data } = await api.get('/permissions');
    return data;
  },

  async upsertPagePermission({ role, pageKey, isVisible }) {
    const { data } = await api.patch('/permissions/page', { role, pageKey, isVisible });
    return data;
  },

  async upsertGraphPermission({ role, graphKey, isVisible }) {
    const { data } = await api.patch('/permissions/graph', { role, graphKey, isVisible });
    return data;
  },

  // Financial Targets (utilisation, avg rate, billables — persisted per year)
  async getFinancialTargets(year) {
    const { data } = await api.get('/financial-targets', { params: { year } });
    return data;
  },

  async upsertFinancialTargets(targetData) {
    const { data } = await api.post('/financial-targets', targetData);
    return data;
  },

  // Tracker (RainTrack) — utilisation proxy
  async getTrackerUtilisation({ from, to } = {}) {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const { data } = await api.get('/tracker/utilisation', { params });
    return data;
  },

  // Monday.com sync
  async syncMonday() {
    const { data } = await api.post('/monday/sync');
    return data;
  },

  async getWbrData() {
    const { data } = await api.get('/monday/wbr');
    return data;
  },

  async syncConsultantsFromTracker() {
    const { data } = await api.post('/consultants/sync-tracker');
    return data;
  },

  async enrichConsultantsFromMonday() {
    const { data } = await api.post('/consultants/enrich-monday');
    return data;
  },

  async getResourceIntelligence() {
    const { data } = await api.get('/consultants/intelligence');
    return data;
  },

  // codex.md v4 — per-consultant monthly utilisation for the Nivo heatmap.
  // Returns [{ id, data: [{ x, y }] }] directly; consumer just passes
  // through an adapter (toHeatmapRows) before rendering.
  async getUtilisationHeatmap(year) {
    const { data } = await api.get('/consultants/utilisation-heatmap', { params: { year } });
    return data;
  },

  async matchConsultantsToProject(requirements) {
    const { data } = await api.post('/consultants/match', requirements);
    return data;
  },

  // Full sync (clients + projects + monday + aggregates)
  async triggerFullSync() {
    const { data } = await api.post('/clients/sync');
    return data;
  },

  async getClientOverview() {
    const { data } = await api.get('/clients/overview');
    return data;
  },

  async getClientDetail(id) {
    const { data } = await api.get(`/clients/detail/${id}`);
    return data;
  },

  // Financial Snapshots
  async getSnapshots(year) {
    const { data } = await api.get('/snapshots', { params: { year } });
    return data;
  },

  async captureSnapshot() {
    const { data } = await api.post('/snapshots/capture');
    return data;
  },

  // NPS summary (from local DB — no Monday API dependency)
  async getNpsSummary() {
    const { data } = await api.get('/clients/nps-summary');
    return data;
  },

  // Board endpoints (Monday.com-like functionality)
  async getBoards() {
    const { data } = await api.get('/boards');
    return data;
  },

  async getBoard(boardId) {
    const { data } = await api.get(`/boards/${boardId}`);
    return data;
  },

  async createBoard(boardData) {
    const { data } = await api.post('/boards', boardData);
    return data;
  },

  async updateBoard(boardId, boardData) {
    const { data } = await api.patch(`/boards/${boardId}`, boardData);
    return data;
  },

  async deleteBoard(boardId) {
    const { data } = await api.delete(`/boards/${boardId}`);
    return data;
  },

  // Board Groups
  async createBoardGroup(boardId, groupData) {
    const { data } = await api.post(`/boards/${boardId}/groups`, groupData);
    return data;
  },

  async updateBoardGroup(boardId, groupId, groupData) {
    const { data } = await api.patch(`/boards/${boardId}/groups/${groupId}`, groupData);
    return data;
  },

  async deleteBoardGroup(boardId, groupId) {
    const { data } = await api.delete(`/boards/${boardId}/groups/${groupId}`);
    return data;
  },

  // Board Columns
  async createBoardColumn(boardId, columnData) {
    const { data } = await api.post(`/boards/${boardId}/columns`, columnData);
    return data;
  },

  async updateBoardColumn(boardId, columnId, columnData) {
    const { data } = await api.patch(`/boards/${boardId}/columns/${columnId}`, columnData);
    return data;
  },

  async deleteBoardColumn(boardId, columnId) {
    const { data } = await api.delete(`/boards/${boardId}/columns/${columnId}`);
    return data;
  },

  // Board Items
  async createBoardItem(boardId, itemData) {
    const { data } = await api.post(`/boards/${boardId}/items`, itemData);
    return data;
  },

  async updateBoardItem(boardId, itemId, itemData) {
    const { data } = await api.patch(`/boards/${boardId}/items/${itemId}`, itemData);
    return data;
  },

  async deleteBoardItem(boardId, itemId) {
    const { data } = await api.delete(`/boards/${boardId}/items/${itemId}`);
    return data;
  },

  // Board Item Values (column values for items)
  async updateBoardItemValue(boardId, itemId, columnId, value) {
    const { data } = await api.patch(`/boards/${boardId}/items/${itemId}/values/${columnId}`, { value });
    return data;
  },

  // Notifications
  async getNotifications() {
    const { data } = await api.get('/notifications');
    return data;
  },

  async queryNotifications(params = {}) {
    const { data } = await api.get('/notifications', { params });
    return data;
  },

  async getMentions(params = {}) {
    const { data } = await api.get('/notifications/mentions', { params });
    return data;
  },

  async markMentionRead(id) {
    const { data } = await api.patch(`/notifications/mentions/${id}/read`);
    return data;
  },

  async markAllMentionsRead() {
    const { data } = await api.patch('/notifications/mentions/read-all');
    return data;
  },

  async markNotificationRead(id) {
    const { data } = await api.patch(`/notifications/${id}/read`);
    return data;
  },

  async markAllNotificationsRead() {
    const { data } = await api.patch('/notifications/read-all');
    return data;
  },

  async clearAllNotifications() {
    const { data } = await api.delete('/notifications');
    return data;
  },

  async createNotification(notificationData) {
    const { data } = await api.post('/notifications', notificationData);
    return data;
  },

  // User profile
  async updateProfile(updates) {
    const { data } = await api.patch('/users/profile', updates);
    return data;
  },
};

export default apiClient;
